import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";

// AppIcons - WACommander's own icon set, drawn as SVG (owner, 2026-09-27: "re-designing the
// emoticons as svgs to make them more professional - do not use the unicode ones"). ONE place for
// every icon, read by BOTH halves - the Bridge imports it the way it imports `AppTaskTips`:
//   - the service writes the SAME markup into the script-free contact page (`ReactionBook.Html`,
//     `TaskStateHtml`) with `Svg` / `Faces`;
//   - the app builds the SAME tree with `Build` / `BuildFaces` (DOM building, `createElementNS`), and
//     where a view is a string template (`AppTaskView`) it inserts `Svg` - a constant of ours.
//   `IconContract.test.ts` serialises what `BuildFaces` built and pins it byte for byte to `Faces`.
//
// ⭐ ONE GRID, TWO LANGUAGES:
//   - every icon is a 24 x 24 viewBox, details on a 1.6-1.8 stroke with round caps and joins;
//   - REACTIONS are filled shapes in restrained colour (a warm yellow face, a red heart) with dark
//     amber details - they are content, like the emoji they stand for, and read the same on the light
//     and the dark pill;
//   - UI GLYPHS (the reaction opener, the tick, the menu, go-to-message, reply, the AI spark) are
//     single-colour line drawings in `currentColor`, so they take the colour of the text around them.
//   Colours are PRESENTATION ATTRIBUTES holding the light values (never a `style` attribute - the app's CSP
//   refuses inline styles), so a page with no stylesheet of ours still draws them right; a class per paint
//   role (`rxp-*`) lets `Css` apply the tokens for the light and the dark theme.
//
// ⛔ THE WIRE VALUE IS STILL THE UNICODE EMOJI. WhatsApp's protocol carries the emoji itself, and
// `Outbox.ReactEmoji`, the receipts, the journal and the backup are untouched - this file only decides
// how six known values are DRAWN. They are matched by code point, ignoring U+FE0F (a phone's bare ❤
// and the row's ❤️ are one heart). Any other emoji a contact reacts with is drawn as TEXT
// (`span.rxt`) through the page's font stack, including the `EmojiFont` web-font fallback - this is
// not a full emoji set and must not become one. Emoji inside message text are the contact's words and
// are never replaced.
//
// ⛔ SECURITY: every SVG here is a CONSTANT of ours. An emoji from the page (`data-others`) or a
// message only CHOOSES one by lookup; it never becomes markup. Unknown text goes in escaped (`Faces`)
// or by `textContent` (`BuildFaces`). Nothing is ever parsed from a string into the DOM.

/** One element of an icon: its tag and its attributes, in the order they are written. */
export class AppIconPart {
    public Tag: string = "";
    public Attrs: string[][] = [];
}

/** One icon: what it is called, what a screen reader hears, and its parts. */
export class AppIcon {
    public Name: string = "";
    /** For a reaction, the screen-reader label ("thumbs up"). For a UI glyph, "" - its button is labelled. */
    public Label: string = "";
    /** A reaction (filled, coloured) or a UI glyph (a line drawing in `currentColor`). */
    public Reaction: boolean = false;
    public Parts: AppIconPart[] = [];
}

/** What `Build` needs of a node - a real `Element` fits by shape, so does a test's fake. */
export interface IIconNode {
    textContent: string | null;
    setAttribute(Name: string, Value: string): void;
    appendChild(Child: IIconNode): unknown;
}

/** What `Build` needs of a document. */
export interface IIconDoc {
    createElement(Tag: string): IIconNode;
    createElementNS(Ns: string, Tag: string): IIconNode;
}

export class AppIcons {
    public static readonly SvgNs: string = "http://www.w3.org/2000/svg";
    /** On every icon's `<svg>`. */
    public static readonly IconClass: string = "wi";
    /** On a reaction icon's `<svg>` too. */
    public static readonly ReactionClass: string = "rxi";
    /** A reaction that is not one of the six: its emoji as text. */
    public static readonly TextClass: string = "rxt";
    /** U+FE0F, the emoji presentation selector - ignored when an emoji is matched to an icon. */
    public static readonly Selector: string = String.fromCharCode(0xFE0F);
    /** The size a reaction is written at in the page's pill; CSS may draw it larger (the picker row). */
    public static readonly PillPx: number = 16;

    // ── The palette ─────────────────────────────────────────────────────────────────────────────
    // ⛔ PRESENTATION ATTRIBUTES, NEVER A `style` ATTRIBUTE (2026-09-27): the app's Content-Security-Policy has no
    // 'unsafe-inline' for styles, so an inline style is refused in the app AND in the contact frame. The attributes
    // carry the LIGHT colours (so a page with no stylesheet of ours still draws them right - a presentation
    // attribute cannot hold `var()`, measured in Chrome), and a class per paint role lets `Css` retheme them
    // (an author rule beats a presentation attribute). Line glyphs are `currentColor` and need no class.
    // DOCUMENTED EXCEPTION (each below): constant attribute lists, read while the class loads.
    // ERROR-SUPPRESSED-JUSTIFIED: they return literals and cannot throw. They are METHODS, not fields, because
    // methods exist before any static field initialiser runs (`Table` below reads them).

    private static Face(): string[] {
        return ["class", "rxp-face", "fill", "#F6C445", "stroke", "#D69E1C", "stroke-width", "1"];
    }
    private static Skin(): string[] {
        return ["class", "rxp-skin", "fill", "#F6C445", "stroke", "#6B4A12", "stroke-width", "1.5", "stroke-linejoin", "round"];
    }
    private static InkFill(): string[] {
        return ["class", "rxp-inkf", "fill", "#6B4A12"];
    }
    private static InkLine(Width: string): string[] {
        return ["class", "rxp-inkl", "fill", "none", "stroke", "#6B4A12", "stroke-width", Width, "stroke-linecap", "round",
            "stroke-linejoin", "round"];
    }
    private static Heart(): string[] {
        return ["class", "rxp-heart", "fill", "#E0464E", "stroke", "#B42F37", "stroke-width", "1", "stroke-linejoin", "round"];
    }
    private static Tongue(): string[] {
        return ["class", "rxp-tongue", "fill", "#E7736B"];
    }
    private static Tear(): string[] {
        return ["class", "rxp-tear", "fill", "#4C9BE0"];
    }
    private static Line(Width: string): string[] {
        return ["fill", "none", "stroke", "currentColor", "stroke-width", Width, "stroke-linecap", "round", "stroke-linejoin", "round"];
    }
    private static Solid(): string[] {
        return ["fill", "currentColor"];
    }

    // ── The six reactions, in `AppReactRules.Emoji`'s order ──────────────────────────────────

    /** ⛔ The KEYS are the six code points WITHOUT U+FE0F; `Key` strips it before a lookup. */
    public static readonly ReactionKeys: string[] = ["\u{1F44D}", "❤", "\u{1F602}", "\u{1F62E}", "\u{1F622}", "\u{1F64F}"];

    private static readonly Table: AppIcon[] = [
        AppIcons.Make("thumbsup", "thumbs up", true, [
            ["rect", "x", "2.8", "y", "10", "width", "4", "height", "10.5", "rx", "1.2", ...AppIcons.Skin()],
            ["path", "d", "M7 11.2 10.3 4.9c.4-.8 1.4-1.2 2.2-.9.9.3 1.4 1.3 1.1 2.2L12.8 9.4h5.5c1.3 0 2.2 1.2 1.9 2.4"
                + "l-1.5 6.8c-.2 1-1.1 1.7-2.1 1.7H7Z", ...AppIcons.Skin()],
            ["path", "d", "M15.4 12.6h3.8M15.1 15.5h3.2", ...AppIcons.InkLine("1.3")],
        ]),
        AppIcons.Make("heart", "heart", true, [
            ["path", "d", "M12 20.2S3.2 15 3.2 8.9C3.2 6.3 5.2 4.2 7.8 4.2c1.8 0 3.3 1 4.2 2.4.9-1.4 2.4-2.4 4.2-2.4"
                + " 2.6 0 4.6 2.1 4.6 4.7 0 6.1-8.8 11.3-8.8 11.3Z", ...AppIcons.Heart()],
        ]),
        AppIcons.Make("laughing", "laughing", true, [
            ["circle", "cx", "12", "cy", "12", "r", "9.6", ...AppIcons.Face()],
            ["path", "d", "M7 10.1c.6-1.4 2.6-1.4 3.2 0M13.8 10.1c.6-1.4 2.6-1.4 3.2 0", ...AppIcons.InkLine("1.7")],
            ["path", "d", "M6.9 12.9h10.2c0 3-2.3 5.3-5.1 5.3s-5.1-2.3-5.1-5.3Z", ...AppIcons.InkFill()],
            ["path", "d", "M9.3 16.8c.7-.7 1.7-1 2.7-1s2 .3 2.7 1c-.8.6-1.7.9-2.7.9s-1.9-.3-2.7-.9Z", ...AppIcons.Tongue()],
        ]),
        AppIcons.Make("surprised", "surprised", true, [
            ["circle", "cx", "12", "cy", "12", "r", "9.6", ...AppIcons.Face()],
            ["path", "d", "M6.9 7.2c.9-.9 2.3-1.2 3.4-.7M17.1 7.2c-.9-.9-2.3-1.2-3.4-.7", ...AppIcons.InkLine("1.4")],
            ["ellipse", "cx", "8.9", "cy", "10.2", "rx", "1.2", "ry", "1.6", ...AppIcons.InkFill()],
            ["ellipse", "cx", "15.1", "cy", "10.2", "rx", "1.2", "ry", "1.6", ...AppIcons.InkFill()],
            ["ellipse", "cx", "12", "cy", "15.7", "rx", "2.1", "ry", "2.6", ...AppIcons.InkFill()],
        ]),
        AppIcons.Make("sad", "sad", true, [
            ["circle", "cx", "12", "cy", "12", "r", "9.6", ...AppIcons.Face()],
            ["ellipse", "cx", "8.9", "cy", "10.2", "rx", "1.15", "ry", "1.45", ...AppIcons.InkFill()],
            ["ellipse", "cx", "15.1", "cy", "10.2", "rx", "1.15", "ry", "1.45", ...AppIcons.InkFill()],
            ["path", "d", "M8.6 17c.8-1.3 2-2 3.4-2s2.6.7 3.4 2", ...AppIcons.InkLine("1.7")],
            ["path", "d", "M17.5 12.3s-1.2 1.6-1.2 2.5c0 .7.5 1.2 1.2 1.2s1.2-.5 1.2-1.2c0-.9-1.2-2.5-1.2-2.5Z", ...AppIcons.Tear()],
        ]),
        AppIcons.Make("thanks", "thanks", true, [
            ["path", "d", "M12 4.2c-.9-.9-2.2-.5-2.5.7L7.8 12.4 5.3 15c-.5.5-.5 1.3 0 1.8l2.7 2.7c.5.5 1.3.5 1.8 0L12 17.3Z",
                ...AppIcons.Skin()],
            ["path", "d", "M12 4.2c.9-.9 2.2-.5 2.5.7l1.7 7.5 2.5 2.6c.5.5.5 1.3 0 1.8L16 19.5c-.5.5-1.3.5-1.8 0L12 17.3Z",
                ...AppIcons.Skin()],
            ["path", "d", "M4.2 6.6 5.6 7.6M3.4 10.2h1.7M19.8 6.6l-1.4 1M20.6 10.2h-1.7", ...AppIcons.InkLine("1.3")],
        ]),
    ];

    // ── The UI glyphs ────────────────────────────────────────────────────────────────────────

    /** The reaction opener (was ☺): a smiling face with a small plus. */
    public static readonly React: AppIcon = AppIcons.Make("react", "", false, [
        ["path", "d", "M20.3 11.2A8.4 8.4 0 1 1 12.8 3.6", ...AppIcons.Line("1.8")],
        ["circle", "cx", "9", "cy", "10.2", "r", "1.15", ...AppIcons.Solid()],
        ["circle", "cx", "14.6", "cy", "10.2", "r", "1.15", ...AppIcons.Solid()],
        ["path", "d", "M8.4 14.2c.8 1.3 2 2 3.4 2s2.6-.7 3.4-2", ...AppIcons.Line("1.8")],
        ["path", "d", "M19 2.6v5.2M16.4 5.2h5.2", ...AppIcons.Line("1.8")],
    ]);
    /** A tick (was ✓). */
    public static readonly Check: AppIcon = AppIcons.Make("check", "", false, [
        ["path", "d", "M5 12.6l4.6 4.6L19 7.8", ...AppIcons.Line("2.2")],
    ]);
    /** The "more" menu (was …). */
    public static readonly More: AppIcon = AppIcons.Make("more", "", false, [
        ["circle", "cx", "5.5", "cy", "12", "r", "1.7", ...AppIcons.Solid()],
        ["circle", "cx", "12", "cy", "12", "r", "1.7", ...AppIcons.Solid()],
        ["circle", "cx", "18.5", "cy", "12", "r", "1.7", ...AppIcons.Solid()],
    ]);
    /** Go to the message a task came from (was ↗). */
    public static readonly Go: AppIcon = AppIcons.Make("go", "", false, [
        ["path", "d", "M7.5 16.5 16.5 7.5M9.5 7.5h7v7", ...AppIcons.Line("2")],
    ]);
    /** A reply owed (was ↩). */
    public static readonly Reply: AppIcon = AppIcons.Make("reply", "", false, [
        ["path", "d", "M9.5 6.5 4.5 11.5l5 5M4.5 11.5h9.5c3.3 0 5.5 2.2 5.5 5.5v1", ...AppIcons.Line("2")],
    ]);
    /** The AI help mark (was ✦). */
    public static readonly Spark: AppIcon = AppIcons.Make("spark", "", false, [
        ["path", "d", "M12 2.8c.6 4.9 4.3 8.6 9.2 9.2-4.9.6-8.6 4.3-9.2 9.2-.6-4.9-4.3-8.6-9.2-9.2 4.9-.6 8.6-4.3 9.2-9.2Z",
            ...AppIcons.Solid()],
    ]);

    /**
     * A contact with no name we can make initials from - a bare number, "Unknown contact" (was an EMPTY
     * grey disc, seen live 2026-09-27). WhatsApp's own default: a head and shoulders, filled in
     * `currentColor`, the shoulders cut by the disc's own circle (r 12 about the centre) so it needs no
     * `overflow:hidden` from the page it is in. Drawn at 100% of its disc (`AppAvatarFace.Inner`).
     */
    public static readonly Person: AppIcon = AppIcons.Make("person", "", false, [
        ["circle", "cx", "12", "cy", "9.6", "r", "4.4", ...AppIcons.Solid()],
        ["path", "d", "M4.2 21.1C5.4 17.6 8.4 15.6 12 15.6s6.6 2 7.8 5.5A12 12 0 0 1 4.2 21.1Z", ...AppIcons.Solid()],
    ]);

    /**
     * A translated message (translation, 2026-09-27): a small flag on its pole - ONE icon for incoming AND outgoing
     * translated bubbles, on the archive page and in the app. Its button shows the original (or what was sent) and
     * Copy (`AppTranslate`). A line glyph in `currentColor`.
     */
    public static readonly Flag: AppIcon = AppIcons.Make("flag", "", false, [
        ["path", "d", "M5.5 21V3.6", ...AppIcons.Line("1.8")],
        ["path", "d", "M5.5 4.2h11.6l-2.3 3.9 2.3 3.9H5.5", ...AppIcons.Line("1.8")],
    ]);

    // ── The chrome set (design system, 2026-09-30: "icons instead of typed arrows") ──────────────
    // ⛔ NO TYPED ARROW OR GLYPH IN OUR UI CHROME (← → ↗ × + ▾ ▸ › ✓): each is one of these line drawings, same grid,
    // same 1.8-2 stroke, `currentColor`, aria-hidden inside a control that carries the words or an aria-label.

    /** Back (was ←). */
    public static readonly Back: AppIcon = AppIcons.Make("back", "", false, [
        ["path", "d", "M19 12H5.5M11 5.5 4.5 12l6.5 6.5", ...AppIcons.Line("2")],
    ]);
    /** Forward, next (was →). */
    public static readonly Forward: AppIcon = AppIcons.Make("forward", "", false, [
        ["path", "d", "M5 12h13.5M13 5.5l6.5 6.5-6.5 6.5", ...AppIcons.Line("2")],
    ]);
    /** Add (was +). */
    public static readonly Plus: AppIcon = AppIcons.Make("plus", "", false, [
        ["path", "d", "M12 5v14M5 12h14", ...AppIcons.Line("2")],
    ]);
    /** Close, dismiss (was ×). */
    public static readonly Close: AppIcon = AppIcons.Make("close", "", false, [
        ["path", "d", "M6.5 6.5l11 11M17.5 6.5l-11 11", ...AppIcons.Line("2")],
    ]);
    /** Search. */
    public static readonly Search: AppIcon = AppIcons.Make("search", "", false, [
        ["circle", "cx", "10.5", "cy", "10.5", "r", "6", ...AppIcons.Line("2")],
        ["path", "d", "M15 15l4.5 4.5", ...AppIcons.Line("2")],
    ]);
    /** Send (a paper plane). */
    public static readonly Send: AppIcon = AppIcons.Make("send", "", false, [
        ["path", "d", "M4 11.6 19.8 4.4 13.4 20l-2.3-6.3L4 11.6ZM11.1 13.7 19.8 4.4", ...AppIcons.Line("1.8")],
    ]);
    /** Download, save a file. */
    public static readonly Download: AppIcon = AppIcons.Make("download", "", false, [
        ["path", "d", "M12 4v11M7 10.5l5 5 5-5M5 19.5h14", ...AppIcons.Line("2")],
    ]);
    /** WhatsApp's archive tray; archiving preserves the messages. */
    public static readonly Archive: AppIcon = AppIcons.Make("archive", "", false, [
        ["rect", "x", "3", "y", "4", "width", "18", "height", "4", "rx", "1", ...AppIcons.Line("1.8")],
        ["path", "d", "M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4", ...AppIcons.Line("1.8")],
    ]);
    /** A favourite chat, distinct from the four-point AI spark. */
    public static readonly Star: AppIcon = AppIcons.Make("star", "favourite", false, [
        ["path", "d", "M12 3l2.78 5.64 6.22.91-4.5 4.39 1.06 6.19L12 17.2l-5.56 2.93 1.06-6.19L3 9.55l6.22-.91L12 3Z", ...AppIcons.Line("1.8")],
    ]);
    /** Copy. */
    public static readonly Copy: AppIcon = AppIcons.Make("copy", "", false, [
        ["rect", "x", "8.5", "y", "8.5", "width", "11", "height", "11", "rx", "2", ...AppIcons.Line("1.8")],
        ["path", "d", "M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2", ...AppIcons.Line("1.8")],
    ]);
    /** Remove for good (a bin): "Remove from history" in the AI panel. */
    public static readonly Trash: AppIcon = AppIcons.Make("trash", "", false, [
        ["path", "d", "M4.5 7h15M9.5 7V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2M6.5 7l.8 11.2a2 2 0 0 0 2 1.8h5.4a2 2 0 0 0 2-1.8L17.5 7M10 11v5M14 11v5", ...AppIcons.Line("1.8")],
    ]);
    /** Open, unfold (was ▾). */
    public static readonly ChevronDown: AppIcon = AppIcons.Make("chevdown", "", false, [
        ["path", "d", "M6.5 9.5 12 15l5.5-5.5", ...AppIcons.Line("2")],
    ]);
    /** Close, fold. */
    public static readonly ChevronUp: AppIcon = AppIcons.Make("chevup", "", false, [
        ["path", "d", "M6.5 14.5 12 9l5.5 5.5", ...AppIcons.Line("2")],
    ]);
    /** Settings (a gear). */
    public static readonly Settings: AppIcon = AppIcons.Make("settings", "", false, [
        ["circle", "cx", "12", "cy", "12", "r", "3", ...AppIcons.Line("1.8")],
        ["path", "d", "M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7"
            + "M16.8 7.2l1.7-1.7", ...AppIcons.Line("1.8")],
        ["circle", "cx", "12", "cy", "12", "r", "6.6", ...AppIcons.Line("1.8")],
    ]);
    /** A pinned chat (a push pin) - the list's mark for a chat pinned on the phone (`ChatActions`). */
    public static readonly Pin: AppIcon = AppIcons.Make("pin", "", false, [
        ["path", "d", "M9 3.8h6l-1 5.4 3.2 3.3v1.8H6.8v-1.8L10 9.2 9 3.8ZM12 14.3v6", ...AppIcons.Line("1.8")],
    ]);
    /** A chat locked on the phone (WhatsApp's Chat Lock). */
    public static readonly Lock: AppIcon = AppIcons.Make("lock", "", false, [
        ["rect", "x", "5.5", "y", "10.5", "width", "13", "height", "9.5", "rx", "2", ...AppIcons.Line("1.8")],
        ["path", "d", "M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5", ...AppIcons.Line("1.8")],
    ]);
    /** A new chat (a speech bubble with a plus). */
    public static readonly NewChat: AppIcon = AppIcons.Make("newchat", "", false, [
        ["path", "d", "M4.5 18.8V6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8.2Z", ...AppIcons.Line("1.8")],
        ["path", "d", "M12 7.8v5.4M9.3 10.5h5.4", ...AppIcons.Line("1.8")],
    ]);
    /** The light theme. */
    public static readonly Sun: AppIcon = AppIcons.Make("sun", "", false, [
        ["circle", "cx", "12", "cy", "12", "r", "4", ...AppIcons.Line("1.8")],
        ["path", "d", "M12 2.8v2M12 19.2v2M2.8 12h2M19.2 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M5.5 18.5l1.4-1.4M17.1 6.9l1.4-1.4",
            ...AppIcons.Line("1.8")],
    ]);
    /** The dark theme. */
    public static readonly Moon: AppIcon = AppIcons.Make("moon", "", false, [
        ["path", "d", "M19.5 14.6A7.8 7.8 0 0 1 9.4 4.5a7.8 7.8 0 1 0 10.1 10.1Z", ...AppIcons.Line("1.8")],
    ]);
    /** Follow the device's theme. */
    public static readonly Monitor: AppIcon = AppIcons.Make("monitor", "", false, [
        ["rect", "x", "3.5", "y", "4.5", "width", "17", "height", "11.5", "rx", "2", ...AppIcons.Line("1.8")],
        ["path", "d", "M9 20h6M12 16v4", ...AppIcons.Line("1.8")],
    ]);
    /** Help (a question mark in a circle) - the top bar's "?". */
    public static readonly Help: AppIcon = AppIcons.Make("help", "", false, [
        ["circle", "cx", "12", "cy", "12", "r", "8.6", ...AppIcons.Line("1.8")],
        ["path", "d", "M9.6 9.6a2.5 2.5 0 1 1 3.6 2.2c-.8.4-1.2 1-1.2 1.8v.6", ...AppIcons.Line("1.8")],
        ["circle", "cx", "12", "cy", "17", "r", "1.1", ...AppIcons.Solid()],
    ]);
    /** A warning (an exclamation mark in a triangle) - the account-warning banner (`AppStanding`). */
    public static readonly Warning: AppIcon = AppIcons.Make("warning", "", false, [
        ["path", "d", "M12 4.2 3.4 19h17.2Z", ...AppIcons.Line("1.8")],
        ["path", "d", "M12 9.8v4.4", ...AppIcons.Line("1.8")],
        ["circle", "cx", "12", "cy", "16.6", "r", "1.1", ...AppIcons.Solid()],
    ]);
    /** A clock - a sent message still waiting to go (the Sent view's state icon). */
    public static readonly Clock: AppIcon = AppIcons.Make("clock", "", false, [
        ["circle", "cx", "12", "cy", "12", "r", "8", ...AppIcons.Line("1.8")],
        ["path", "d", "M12 7.6V12l3 2", ...AppIcons.Line("1.8")],
    ]);

    /** A place (a map pin) - the contact's address in Chat settings' About. */
    public static readonly Place: AppIcon = AppIcons.Make("place", "", false, [
        ["path", "d", "M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11Z", ...AppIcons.Line("1.8")],
        ["circle", "cx", "12", "cy", "10", "r", "2.4", ...AppIcons.Line("1.8")],
    ]);
    /** A globe - the contact's website in Chat settings' About. */
    public static readonly Globe: AppIcon = AppIcons.Make("globe", "", false, [
        ["circle", "cx", "12", "cy", "12", "r", "8.6", ...AppIcons.Line("1.8")],
        ["path", "d", "M3.4 12h17.2M12 3.4c2.4 2.4 3.6 5.3 3.6 8.6s-1.2 6.2-3.6 8.6c-2.4-2.4-3.6-5.3-3.6-8.6S9.6 5.8 12 3.4Z", ...AppIcons.Line("1.8")],
    ]);
    /** An envelope - the contact's email in Chat settings' About. */
    public static readonly Mail: AppIcon = AppIcons.Make("mail", "", false, [
        ["rect", "x", "3.5", "y", "5.5", "width", "17", "height", "13", "rx", "2", ...AppIcons.Line("1.8")],
        ["path", "d", "M4 7l8 6 8-6", ...AppIcons.Line("1.8")],
    ]);

    // ── The navigation rail (business look, wave B, 2026-10-13) - the five views, same grid, a 1.8 line ──────────
    /** Chats: a speech bubble with two lines. */
    public static readonly NavChats: AppIcon = AppIcons.Make("navchats", "", false, [
        ["path", "d", "M4.5 5.5h15a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H11l-4.5 3.5v-3.5h-2A1.5 1.5 0 0 1 3 16V7a1.5 1.5 0 0 1 1.5-1.5Z", ...AppIcons.Line("1.8")],
        ["path", "d", "M7.5 10h9M7.5 13.5h5.5", ...AppIcons.Line("1.8")],
    ]);
    /** Overview: three bars on a base line. */
    public static readonly NavOverview: AppIcon = AppIcons.Make("navoverview", "", false, [
        ["path", "d", "M4 20h16", ...AppIcons.Line("1.8")],
        ["path", "d", "M6.5 16.5v-5M12 16.5V5.5M17.5 16.5V9", ...AppIcons.Line("1.8")],
    ]);
    /** Tasks: a ticked box. */
    public static readonly NavTasks: AppIcon = AppIcons.Make("navtasks", "", false, [
        ["rect", "x", "4", "y", "4", "width", "16", "height", "16", "rx", "2.5", ...AppIcons.Line("1.8")],
        ["path", "d", "M8 12.3l2.8 2.8L16 9.6", ...AppIcons.Line("1.8")],
    ]);
    /** Revenue: a banknote. */
    public static readonly NavRevenue: AppIcon = AppIcons.Make("navrevenue", "", false, [
        ["rect", "x", "3", "y", "6.5", "width", "18", "height", "11", "rx", "2", ...AppIcons.Line("1.8")],
        ["circle", "cx", "12", "cy", "12", "r", "2.4", ...AppIcons.Line("1.8")],
        ["path", "d", "M6.5 10v4M17.5 10v4", ...AppIcons.Line("1.8")],
    ]);
    /** Sent: a paper plane. */
    public static readonly NavSent: AppIcon = AppIcons.Make("navsent", "", false, [
        ["path", "d", "M20.5 3.5 10.5 13.5", ...AppIcons.Line("1.8")],
        ["path", "d", "M20.5 3.5 14 20.5l-3.5-7-7-3.5 17-6.5Z", ...AppIcons.Line("1.8")],
    ]);

    /**
     * ⭐ THE BRAND MARK (the favicon's drawing on the 24 grid): a rounded square with a "W". NOT a line glyph and not in
     * `Glyphs()`/`Chrome()`: its two fills are presentation attributes holding the light values (the reactions' rule - no
     * inline style), and `app.css` re-points them at the tokens (`.wi-mark .mark-bg` = accent, `.mark-fg` = on-accent).
     */
    public static readonly Mark: AppIcon = AppIcons.Make("mark", "", false, [
        ["rect", "class", "mark-bg", "width", "24", "height", "24", "rx", "5.25", "fill", "#095495"],
        ["path", "class", "mark-fg", "d", "M5.25 8.25h3l1.5 5.25 1.5-5.25h1.5l1.5 5.25 1.5-5.25h3l-3 9h-3l-.75-3-.75 3h-3z", "fill", "#ffffff"],
    ]);

    /**
     * Builds one icon from its name, label and parts. DOCUMENTED EXCEPTION: runs once per icon while
     * the class loads, over constants of this file. ERROR-SUPPRESSED-JUSTIFIED: pure construction.
     */
    private static Make(Name: string, Label: string, Reaction: boolean, Parts: string[][]): AppIcon {
        const Out: AppIcon = new AppIcon();
        Out.Name = Name;
        Out.Label = Label;
        Out.Reaction = Reaction;
        for (const One of Parts) {
            const Part: AppIconPart = new AppIconPart();
            Part.Tag = One[0];
            for (let At: number = 1; At + 1 < One.length; At = At + 2) { Part.Attrs.push([One[At], One[At + 1]]); }
            Out.Parts.push(Part);
        }
        return Out;
    }

    // ── Lookup ───────────────────────────────────────────────────────────────────────────────

    /** An emoji as its lookup key: U+FE0F (the emoji presentation selector) removed, trimmed. */
    public static Key = (Emoji: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⚠ BY CODE, NOT AS AN ESCAPE IN A LITERAL: the edit tooling turns "️" into the invisible
            // character itself (CLAUDE.md, the `HeaderSafe` hazard) - `Selector` cannot be normalised away.
            return nz(Emoji).split(AppIcons.Selector).join("").trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Key");
            return "";
        }
    };

    /** Our icon for a reaction emoji, or null when it is not one of the six (it stays text). */
    public static ForEmoji = (Emoji: string): AppIcon | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const At: number = AppIcons.ReactionKeys.indexOf(AppIcons.Key(Emoji));
            if (At < 0) { return null; }
            return AppIcons.Table[At];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.ForEmoji");
            return null;
        }
    };

    /** The six reaction icons, in `AppReactRules.Emoji`'s order (the design sheet, the tests). */
    public static Reactions = (): AppIcon[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return AppIcons.Table.slice();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Reactions");
            return [];
        }
    };

    /** The UI glyphs (the design sheet, the tests). */
    public static Glyphs = (): AppIcon[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return [AppIcons.React, AppIcons.Check, AppIcons.More, AppIcons.Go, AppIcons.Reply, AppIcons.Spark, AppIcons.Person, AppIcons.Flag]
                .concat(AppIcons.Chrome());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Glyphs");
            return [];
        }
    };

    /** The chrome set - the icons that replaced typed arrows and glyphs (the style guide, the tests). */
    public static Chrome = (): AppIcon[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return [AppIcons.Back, AppIcons.Forward, AppIcons.Plus, AppIcons.Close, AppIcons.Search, AppIcons.Send, AppIcons.Download,
                AppIcons.Copy, AppIcons.Star, AppIcons.ChevronDown, AppIcons.ChevronUp, AppIcons.Settings, AppIcons.Sun, AppIcons.Moon, AppIcons.Monitor,
                AppIcons.Help, AppIcons.Trash, AppIcons.Warning, AppIcons.Clock, AppIcons.Archive,
                AppIcons.NavChats, AppIcons.NavOverview, AppIcons.NavTasks, AppIcons.NavRevenue, AppIcons.NavSent,
                AppIcons.Pin, AppIcons.Lock, AppIcons.NewChat];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Chrome");
            return [];
        }
    };

    /**
     * How a tooltip names one person's reaction: "Anna (thumbs up)" - the six by their words, any
     * other emoji as itself (a tooltip is plain text; it cannot draw an icon).
     */
    public static WhoSaid = (Who: string, Emoji: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Icon: AppIcon | null = AppIcons.ForEmoji(Emoji);
            let Word: string = nz(Emoji).trim();
            if (Icon !== null) { Word = Icon.Label; }
            return nz(Who) + " (" + Word + ")";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.WhoSaid");
            return "";
        }
    };

    // ── Markup (the page) ────────────────────────────────────────────────────────────────────

    /**
     * The attributes of an icon's `<svg>`, in the order both `Svg` and `Build` write them. A reaction
     * is an image with a name (`role="img"`, `aria-label`); a UI glyph is decoration inside a labelled
     * control (`aria-hidden`). `Px` 0 = no size (the stylesheet sizes it). `Size` (a CSS length such as
     * "100%") wins over `Px` - the avatar silhouette fills whatever disc it is drawn in, with no CSS rule.
     */
    public static RootAttrs = (Icon: AppIcon, Px: number, Size: string = ""): string[][] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            let Cls: string = AppIcons.IconClass + " wi-" + Icon.Name;
            if (Icon.Reaction === true) { Cls = Cls + " " + AppIcons.ReactionClass; }
            const Out: string[][] = [["class", Cls], ["viewBox", "0 0 24 24"]];
            if (Size.length > 0) {
                Out.push(["width", Size]);
                Out.push(["height", Size]);
            } else if (Px > 0) {
                Out.push(["width", String(Px)]);
                Out.push(["height", String(Px)]);
            }
            if (Icon.Reaction === true) {
                Out.push(["role", "img"]);
                Out.push(["aria-label", Icon.Label]);
            } else {
                Out.push(["aria-hidden", "true"]);
            }
            Out.push(["focusable", "false"]);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.RootAttrs");
            return [];
        }
    };

    /** One icon as markup - a constant of ours, safe to put into a page or a string template. */
    /**
     * An arrow key as a `<kbd>`: the chevron icon (no typed arrows - the design system) with its name for a screen
     * reader. ONE home for the keyboard help (`AppKeyRules.Keys`) and the three-pane hint (`AppThreeColView`).
     */
    public static ArrowKey = (Up: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Up === true) { return "<kbd>" + AppIcons.Svg(AppIcons.ChevronUp, 14) + "<span class=\"sr\">Up arrow</span></kbd>"; }
            return "<kbd>" + AppIcons.Svg(AppIcons.ChevronDown, 14) + "<span class=\"sr\">Down arrow</span></kbd>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.ArrowKey");
            return "";
        }
    };

    public static Svg = (Icon: AppIcon, Px: number, Size: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<svg" + AppIcons.AttrText(AppIcons.RootAttrs(Icon, Px, Size)) + ">"];
            for (const Part of Icon.Parts) { Out.push("<" + Part.Tag + AppIcons.AttrText(Part.Attrs) + "></" + Part.Tag + ">"); }
            Out.push("</svg>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Svg");
            return "";
        }
    };

    /**
     * A reaction pill's faces as markup: each of the six as our icon, anything else as escaped text in
     * `span.rxt`. ⛔ `BuildFaces` must build exactly this - `IconContract.test.ts`.
     */
    public static Faces = (Emoji: string[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (const One of Emoji) {
                const Icon: AppIcon | null = AppIcons.ForEmoji(One);
                if (Icon !== null) { Out.push(AppIcons.Svg(Icon, AppIcons.PillPx)); continue; }
                Out.push("<span class=\"" + AppIcons.TextClass + "\">" + kHTMLCore.EscapeHtml(nz(One)) + "</span>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Faces");
            return "";
        }
    };

    private static AttrText = (Attrs: string[][]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            for (const One of Attrs) { Out = Out + " " + One[0] + "=\"" + kHTMLCore.EscapeAttr(One[1]) + "\""; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.AttrText");
            return "";
        }
    };

    // ── DOM building (the app) ───────────────────────────────────────────────────────────────

    /** One icon as a DOM tree - `Svg`'s markup, built node by node with `createElementNS`. */
    public static Build = (Doc: IIconDoc, Icon: AppIcon, Px: number): IIconNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Root: IIconNode = Doc.createElementNS(AppIcons.SvgNs, "svg");
            for (const One of AppIcons.RootAttrs(Icon, Px)) { Root.setAttribute(One[0], One[1]); }
            for (const Part of Icon.Parts) {
                const Node: IIconNode = Doc.createElementNS(AppIcons.SvgNs, Part.Tag);
                for (const One of Part.Attrs) { Node.setAttribute(One[0], One[1]); }
                Root.appendChild(Node);
            }
            return Root;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.Build");
            return null;
        }
    };

    /** `Faces`, built into `Parent`: our icon for each of the six, `span.rxt` text for anything else. */
    public static BuildFaces = (Doc: IIconDoc, Parent: IIconNode, Emoji: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of Emoji) {
                const Icon: AppIcon | null = AppIcons.ForEmoji(One);
                if (Icon !== null) {
                    const Node: IIconNode | null = AppIcons.Build(Doc, Icon, AppIcons.PillPx);
                    if (Node !== null) { Parent.appendChild(Node); }
                    continue;
                }
                const Text: IIconNode = Doc.createElement("span");
                Text.setAttribute("class", AppIcons.TextClass);
                Text.textContent = nz(One);
                Parent.appendChild(Text);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIcons.BuildFaces");
        }
    };

    // ── The tokens ───────────────────────────────────────────────────────────────────────────

    /**
     * The reaction colours for the light and the dark theme, and the icons' box. The icons carry the
     * light values as fallbacks, so this only tunes them (dark: a slightly deeper yellow and a brighter
     * heart, so neither glows on a dark pill). DOCUMENTED EXCEPTION: a constant stylesheet.
     * ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        return ":root{--rx-face:#F6C445;--rx-edge:#D69E1C;--rx-ink:#6B4A12;--rx-heart:#E0464E;--rx-heart-edge:#B42F37;"
            + "--rx-tongue:#E7736B;--rx-tear:#4C9BE0}"
            + "svg.wi{display:inline-block;flex:none;vertical-align:-.15em;overflow:visible}"
            + "svg.wi .rxp-face{fill:var(--rx-face,#F6C445);stroke:var(--rx-edge,#D69E1C)}"
            + "svg.wi .rxp-skin{fill:var(--rx-face,#F6C445);stroke:var(--rx-ink,#6B4A12)}"
            + "svg.wi .rxp-inkf{fill:var(--rx-ink,#6B4A12)}svg.wi .rxp-inkl{stroke:var(--rx-ink,#6B4A12)}"
            + "svg.wi .rxp-heart{fill:var(--rx-heart,#E0464E);stroke:var(--rx-heart-edge,#B42F37)}"
            + "svg.wi .rxp-tongue{fill:var(--rx-tongue,#E7736B)}svg.wi .rxp-tear{fill:var(--rx-tear,#4C9BE0)}"
            + "@media(prefers-color-scheme:dark){:root:not([data-theme=\"light\"]){" + AppIcons.DarkTokens() + "}}"
            // ⭐ THE OWNER'S OWN CHOICE (`AppTheme`, 2026-09-30): the frame's <html> carries data-theme="dark" too.
            + ":root[data-theme=\"dark\"]{" + AppIcons.DarkTokens() + "}";
    };

    /** The reaction tokens' dark values. DOCUMENTED EXCEPTION: a constant. ERROR-SUPPRESSED-JUSTIFIED: returns a literal. */
    private static DarkTokens = (): string => {
        return "--rx-face:#EDBB3E;--rx-edge:#B98714;--rx-ink:#5A3D0C;--rx-heart:#F05A62;--rx-heart-edge:#C63E47;"
            + "--rx-tongue:#E6776F;--rx-tear:#62AAEA";
    };
}
